export const STAFF_DOCUMENT_CATEGORIES = [
  'Criminal Background Check',
  'First Aid & CPR Certification',
  'Child Abuse & Neglect Initial Review',
  'Staff Orientation',
  'Health Appraisal',
];

export const EXPIRY_NOTE_PREFIX = 'Expires: ';

export const DISPLAY_STATUSES = {
  MISSING: 'Missing',
  PENDING: 'Pending',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  DENIED: 'Denied',
};

export const STAFF_DOCUMENT_FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'missing', label: 'Missing' },
  { id: 'expired', label: 'Expired' },
  { id: 'denied', label: 'Denied' },
];

function startOfDay(date) {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
}

export function formatExpiryNote(date) {
  const dateString = normalizeExpiryDate(date);
  if (!dateString) return null;

  return `${EXPIRY_NOTE_PREFIX}${dateString}`;
}

export function normalizeExpiryDate(date) {
  if (!date) return null;

  if (typeof date === 'string') {
    const isoMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];

    const parsedDate = new Date(date);
    if (!Number.isNaN(parsedDate.getTime())) return formatIsoDate(parsedDate);

    return null;
  }

  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return formatIsoDate(date);
  }

  return null;
}

function formatIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseExpiryFromNote(note) {
  if (!note || typeof note !== 'string') return null;

  const match = note.match(/Expires:\s*(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export function isExpired(note, today = new Date()) {
  const isoDate = parseExpiryFromNote(note);
  if (!isoDate) return false;

  const expiryDate = startOfDay(new Date(`${isoDate}T00:00:00`));
  const todayStart = startOfDay(today);
  return expiryDate.getTime() < todayStart.getTime();
}

export function deriveDisplayStatus(attachment, today = new Date()) {
  if (!attachment) return DISPLAY_STATUSES.MISSING;

  if (isExpired(attachment.note, today)) return DISPLAY_STATUSES.EXPIRED;

  if (attachment.status === 'Accepted') return DISPLAY_STATUSES.ACTIVE;
  if (attachment.status === 'Rejected') return DISPLAY_STATUSES.DENIED;
  if (attachment.status === 'Submitted') return DISPLAY_STATUSES.PENDING;

  return DISPLAY_STATUSES.PENDING;
}

export function pickPrimaryAttachmentForCategory(attachments) {
  if (!attachments.length) return null;

  return [...attachments].sort(
    (firstAttachment, secondAttachment) =>
      new Date(secondAttachment.created_at) - new Date(firstAttachment.created_at),
  )[0];
}

export function buildStaffDocumentRows(
  categories = STAFF_DOCUMENT_CATEGORIES,
  attachments = [],
  today = new Date(),
) {
  return categories.map((category) => {
    const categoryAttachments = attachments.filter(
      (attachment) => attachment.category === category,
    );
    const attachment = pickPrimaryAttachmentForCategory(categoryAttachments);

    if (!attachment) {
      return {
        attachment: null,
        category,
        emptyLabel: 'No documents uploaded',
        status: DISPLAY_STATUSES.MISSING,
      };
    }

    return {
      attachment,
      category,
      emptyLabel: null,
      expiresAt: parseExpiryFromNote(attachment.note),
      fileName: attachment.file?.metadata?.filename,
      status: deriveDisplayStatus(attachment, today),
      uploadedAt: attachment.created_at,
    };
  });
}

export function filterStaffDocumentRows(rows, { activeTab = 'all', searchQuery = '' } = {}) {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (activeTab !== 'all') {
      const tabStatus = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
      if (row.status !== tabStatus) return false;
    }

    if (!normalizedQuery) return true;

    const haystack = [row.category, row.fileName].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalizedQuery);
  });
}

export function countRowsByStatus(rows) {
  return rows.reduce((counts, row) => {
    counts[row.status] = (counts[row.status] || 0) + 1;
    return counts;
  }, {});
}
